import { useImperativeHandle, useRef } from 'react'
import { object, bool, number, func, shape, string, arrayOf, oneOfType } from 'prop-types'
import { FormHelperText, FormLabel, Box } from '@mui/material'
import ClassicEditor from 'ckeditor5-custom-build/build/ckeditor'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { deepMerge, nonInProduzione } from 'utils'
import useControllerSeServe from './hooks/ControllerSeServeHook'
import useInputGenerico from './hooks/InputGenericoHook'

BaseEditor.propTypes = {
  ...useInputGenerico.propTypes,
  inputRef: object,
  mostraInspector: bool,
  containerSx: object,
  height: oneOfType([number, string]),
  config: object,
  attivaAltreFormattazioni: bool,
  attivaTitoli: bool,
  attivaListe: bool,
  attivaTabelle: bool,
  attivaAllineamenti: bool,
  attivaImmagini: bool,
  attivaLink: bool,
  attivaFunzioniAvanzate: bool,
  uploadFile: func,
  imageReplaceTokenConfig: shape({
    tokenPlaceholder: string.isRequired,
    token: string.isRequired,
  }),
  forcePastePlainText: bool,
  pluginAggiuntivi: arrayOf(shape({
    plugin: func.isRequired,
    config: object,
  })),
}

export default function BaseEditor(props) {
  const {
    label,
    containerSx,
    ...restProps
  } = props

  const {
    required,
    error,
    warning,
    helperText,
    hidden,
    ...inputGenericoProps
  } = useInputGenerico({
    componenteControllato: true,
    underlyingComponent: 'editor',
    ...restProps
  })

  const {
    value,
    onBlur,
    onChange,
    propsRimanenti
  } = useControllerSeServe(inputGenericoProps)

  return (
    <Box
      sx={{
        textAlign: 'left',
        ...(hidden && { display: 'none' }),
        ...containerSx
      }}
    >
      <FormLabel
        required={required}
        error={error}
        {...(warning && { sx: { color: 'warning.main' } })}
      >
        {label}
      </FormLabel>

      <Box
        sx={{
          border: 2,
          borderColor: 'grey.400',
          borderRadius: 1,
          ...(warning && { borderColor: 'warning.main' }),
          ...(error && { borderColor: 'error.main' })
        }}
      >
        <Editor
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          {...propsRimanenti}
        />
      </Box>

      <FormHelperText
        error={error}
        {...(warning && { sx: { color: 'warning.main' } })}
      >
        {helperText}
      </FormHelperText>
    </Box>
  )
}



function Editor(props) {
  const {
    value,
    onBlur,
    onChange,
    disabled,
    inputRef,
    mostraInspector,

    height = 500,
    config: configEsterna,
    attivaAltreFormattazioni,
    attivaTitoli,
    attivaListe,
    attivaTabelle,
    attivaAllineamenti,
    attivaImmagini,
    attivaLink,
    attivaFunzioniAvanzate,
    uploadFile,
    imageReplaceTokenConfig,
    forcePastePlainText,
    pluginAggiuntivi,

    ...restProps
  } = props

  const flagAttivazioniToolbar = {
    attivaAltreFormattazioni,
    attivaTitoli,
    attivaListe,
    attivaTabelle,
    attivaAllineamenti,
    attivaImmagini,
    attivaLink,
    attivaFunzioniAvanzate,
    uploadFile,
    imageReplaceTokenConfig,
    forcePastePlainText,
    pluginAggiuntivi,
  }

  function onEditorChange(event, editor) {
    onChange(editor.getData().trim())
  }

  const editorRef = useRef()

  useImperativeHandle(inputRef, () => ({
    eseguiComando: (nomeComando, propsComando) => {
      const editor = editorRef.current
      editor.execute(nomeComando, propsComando)
    }
  }))

  return (
    <CKEditor
      editor={ClassicEditor}
      config={creaConfig(configEsterna, flagAttivazioniToolbar)}
      debug={nonInProduzione()}

      data={value}
      onBlur={onBlur}
      onChange={onEditorChange}
      disabled={disabled}

      onReady={editor => {
        editor.editing.view.change(writer => {
          const heightDefinitiva = typeof height === 'number' ? `${height}px` : height
          writer.setStyle('height', heightDefinitiva, editor.editing.view.document.getRoot())
        })
        editorRef.current = editor
        if (mostraInspector) ClassicEditor.Dev.Inspector.attach(editor)
      }}

      {...restProps}
    />
  )
}



function creaConfig(configEsterna, flagAttivazioniToolbar) {
  const {
    attivaAltreFormattazioni,
    attivaTitoli,
    attivaListe,
    attivaTabelle,
    attivaAllineamenti,
    attivaImmagini,
    attivaLink,
    attivaFunzioniAvanzate,
    uploadFile,
    imageReplaceTokenConfig,
    forcePastePlainText,
    pluginAggiuntivi = [],
  } = flagAttivazioniToolbar

  function CustomUploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = loader => new CustomUploadAdapter(loader, uploadFile)
  }

  let extraPlugin = []
  let configExtraPlugin = {}
  pluginAggiuntivi.forEach(({ plugin, config }) => {
    extraPlugin.push(plugin)
    configExtraPlugin = { ...configExtraPlugin, ...config }
  })

  return deepMerge({
    htmlSupport: {
      allow: [
        {
          // name: /^(div|p|table|tbody|td|tr|th|h1|h2|h3|h4|h5|h6)$/,
          name: /.*/,
          attributes: true,
          classes: true,
          styles: true
        },
      ]
    },
    image: {
      upload: {
        types: ['png', 'jpeg']
      },
      insert: {
        type: 'inline'
      }
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableCellProperties',
        'tableProperties'
      ]
    },
    fontSize: {
      options: ['default', 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
      supportAllValues: true
    },
    fontColor: {
      colors: COLORI
    },
    fontBackgroundColor: {
      colors: COLORI
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      ]
    },
    toolbar: {
      items: [
        'bold', 'italic', 'underline',
        ...(attivaAltreFormattazioni ? ['|', 'fontBackgroundColor', 'fontColor', 'fontSize', 'removeFormat'] : []),
        ...(attivaTitoli ? ['|', 'heading'] : []),
        ...(attivaAllineamenti ? ['|', 'alignment', 'indent', 'outdent'] : []),
        ...(attivaListe ? ['|', 'bulletedList', 'numberedList'] : []),
        ...(attivaTabelle ? ['|', 'insertTable'] : []),
        ...(attivaImmagini ? ['|', 'imageInsert'] : []),
        ...(attivaLink ? ['|', 'link'] : []),
        '|', 'undo', 'redo', 'findAndReplace',
        ...(attivaFunzioniAvanzate ? ['|', 'sourceEditing'] : [])

        /*
          Opzioni relative a plugin non inclusi nella build corrente di ckeditor
          Se serve includerli:
          - andare in ckeditor/src/ckeditor.js
          - decommentare gli import e le voci corrispondenti dentro Editor.builtinPlugins
          - rifare la build andando nella cartella ckeditor e lanciando npm run build
        */
        // 'pageBreak',
        // 'specialCharacters',
        // 'htmlEmbed',
        // 'style',
        // 'textPartLanguage',
        // 'fontFamily',
        // 'blockQuote',
        // 'todoList',
      ],
      shouldNotGroupWhenFull: true
    },
    removePlugins: [
      ...((uploadFile || !attivaImmagini) ? ['Base64UploadAdapter'] : [])
    ],
    extraPlugins: [
      ...(uploadFile ? [CustomUploadAdapterPlugin] : []),
      ...(imageReplaceTokenConfig ? [ImageReplaceTokenPlugin] : []),
      ...(forcePastePlainText ? [ForcePastePlainTextPlugin] : []),
      ...extraPlugin
    ],
    ...(imageReplaceTokenConfig && { imageReplaceTokenConfig }),
    ...configExtraPlugin
  }, configEsterna)
}



class CustomUploadAdapter {
  constructor(loader, uploadFile) {
    // The file loader instance to use during the upload.
    this.loader = loader
    this.uploadFile = uploadFile
  }

  // Starts the upload process.
  async upload() {
    const loader = this.loader
    const file = await loader.file
    const { ok, data, error } = await this.uploadFile(file, onUploadProgress)
    if (ok) return { default: data.url }
    throw error.message || `Errore nel caricamento del file: ${file.name}`

    function onUploadProgress(progressEvent) {
      const { loaded, total } = progressEvent
      loader.uploadTotal = total
      loader.uploaded = loaded
    }
  }

  // Aborts the upload process.
  abort() {

  }
}



// Plugin per sostituire un placeholder del token nelle src delle immagini
// Agisce solo in fase di visualizzazione senza modificare la src sottostante
function ImageReplaceTokenPlugin(editor) {
  // Ottengo la configurazione del plugin
  const { tokenPlaceholder, token } = editor.config.get('imageReplaceTokenConfig')
  const tokenPlaceholderEncoded = encodeURIComponent(tokenPlaceholder)

  // Agisco nella fase di conversione da model a view
  editor.conversion.for('downcast').add(dispatcher => {
    // Evento lanciato quando viene renderizzata un'immagine con attributo src
    dispatcher.on('attribute:src:imageInline', handler, { priority: 'low' })
  })

  function handler(event, data, conversionApi) {
    // Ignoro le immagini con src in base64
    const src = data.attributeNewValue
    if (src.startsWith('data:')) return

    // Ottengo l'elemento immagine da manipolare
    const viewFigure = conversionApi.mapper.toViewElement(data.item)
    const viewImage = viewFigure.getChild(0)
    if (!viewImage) return

    // Sostituisco il placeholder col token
    const newSrc = src.replace(tokenPlaceholder, token).replace(tokenPlaceholderEncoded, token)
    conversionApi.writer.setAttribute('src', newSrc, viewImage)
  }
}



// Plugin che forza il copia incolla come solo testo
function ForcePastePlainTextPlugin(editor) {
  editor.editing.view.document.on('clipboardInput', (event, data) => {
    // Non spacco il copia incolla di immagini
    if (data.dataTransfer.files.length > 0) return

    // Blocco il comportamento di default
    event.stop()

    // Inserisco il contenuto nella posizione del cursore
    editor.model.change(writer => {
      const plainText = data.dataTransfer.getData('text/plain')
      const insertPosition = editor.model.document.selection.getFirstPosition()
      writer.insertText(plainText, insertPosition)
    })
  })
}



const COLORI = [
  {
    color: 'rgb(0, 0, 0)',
    label: 'Black'
  },
  {
    color: 'rgb(77, 77, 77)',
    label: 'Dim grey'
  },
  {
    color: 'rgb(153, 153, 153)',
    label: 'Grey'
  },
  {
    color: 'rgb(230, 230, 230)',
    label: 'Light grey'
  },
  {
    color: 'rgb(255, 255, 255)',
    label: 'White',
    hasBorder: true
  },
  {
    color: 'rgb(230, 76, 76)',
    label: 'Red'
  },
  {
    color: 'rgb(230, 153, 76)',
    label: 'Orange'
  },
  {
    color: 'rgb(230, 230, 76)',
    label: 'Yellow'
  },
  {
    color: 'rgb(153, 230, 76)',
    label: 'Light green'
  },
  {
    color: 'rgb(76, 230, 76)',
    label: 'Green'
  },
  {
    color: 'rgb(76, 230, 153)',
    label: 'Aquamarine'
  },
  {
    color: 'rgb(76, 230, 230)',
    label: 'Turquoise'
  },
  {
    color: 'rgb(76, 153, 230)',
    label: 'Light blue'
  },
  {
    color: 'rgb(76, 76, 230)',
    label: 'Blue'
  },
  {
    color: 'rgb(153, 76, 230)',
    label: 'Purple'
  }
]